import axios from 'axios';
import store from '@/store';
import { isHogcApiUrl } from './hogc-api-origin';

/**
 * Fix B — Global axios request interceptor.
 *
 * Defensive-only: if a caller already attached an `Authorization: Bearer ...`
 * header whose token is falsy (undefined / null / empty / literal "undefined"
 * from a botched template string), either substitute the current store token
 * or strip the header entirely so the backend never receives the literal
 * string "Bearer undefined" / "Bearer null" / "Bearer ".
 *
 * Scoped strictly to HOGC-owned API hosts (VUE_APP_API_URL +
 * VUE_APP_GIVING_API_URL + VUE_APP_GIVING_API_V2_URL). All other hosts —
 * Storyblok, Datadog, OneSignal, Crisp, Marker, arbitrary CDNs — pass
 * through untouched so we can never accidentally leak a HOGC bearer token
 * into a third-party request, and so unauthenticated public APIs don't
 * get their headers mutated.
 *
 * Intentionally does NOT auto-attach Authorization to requests that didn't
 * provide one — that would change behavior for third-party / unauthenticated
 * endpoints and is outside this fix's scope.
 */

const BEARER_PREFIX = 'Bearer ';

const extractToken = headerValue => {
  if (typeof headerValue !== 'string') return '';
  if (!headerValue.startsWith(BEARER_PREFIX)) return '';
  return headerValue.slice(BEARER_PREFIX.length).trim();
};

const isFalsyTokenString = token => {
  if (!token) return true;
  // Literal strings that indicate a botched template-string interpolation.
  const bad = ['undefined', 'null', 'NaN', 'false'];
  return bad.includes(token);
};

let requestInterceptorId = null;

export function initAuthHeaderInterceptor() {
  if (requestInterceptorId !== null) {
    axios.interceptors.request.eject(requestInterceptorId);
    requestInterceptorId = null;
  }

  requestInterceptorId = axios.interceptors.request.use(
    config => {
      try {
        const headers = config.headers || {};
        const existing = headers.Authorization || headers.authorization;
        if (!existing) return config;

        const providedToken = extractToken(existing);
        if (!isFalsyTokenString(providedToken)) return config;

        // Only operate on HOGC-owned API hosts. Leaving headers on third-party
        // requests untouched prevents the HOGC token from ever being leaked
        // into a request bound for Storyblok / OneSignal / Crisp / etc.
        if (!isHogcApiUrl(config.url, config.baseURL)) {
          console.warn(
            '[Fix-B AuthHeader] Skipping non-HOGC URL with falsy Bearer header:',
            config.method?.toUpperCase(),
            config.url,
          );
          return config;
        }

        // Caller tried to send a broken Bearer header to a HOGC API. Prefer
        // the store token if available; otherwise strip so the request goes
        // out unauthenticated and fails cleanly (401 handled elsewhere).
        const storeToken = store?.state?.user?.token;
        if (storeToken && !isFalsyTokenString(storeToken)) {
          headers.Authorization = `${BEARER_PREFIX}${storeToken}`;
          console.warn(
            '[Fix-B AuthHeader] Replaced falsy Bearer header with store token for',
            config.method?.toUpperCase(),
            config.url,
          );
        } else {
          delete headers.Authorization;
          delete headers.authorization;
          console.warn(
            '[Fix-B AuthHeader] Stripped falsy Bearer header (no store token available) for',
            config.method?.toUpperCase(),
            config.url,
          );
        }
        config.headers = headers;
      } catch (err) {
        console.error('[Fix-B AuthHeader] Interceptor error:', err);
      }
      return config;
    },
    error => Promise.reject(error),
  );
  console.log('[Fix-B AuthHeader] Request interceptor installed (strip-only, HOGC-hosts only)');
}

export function removeAuthHeaderInterceptor() {
  if (requestInterceptorId !== null) {
    axios.interceptors.request.eject(requestInterceptorId);
    requestInterceptorId = null;
  }
}
